import { useSyncExternalStore } from 'react'

export const MOBILE_VIEWPORT_QUERY = '(max-width: 1024px)'

type MediaListener = (event: MediaQueryListEvent) => void

let mediaQueryList: MediaQueryList | null = null
let currentMatches = false
let mediaListener: MediaListener | null = null
const subscribers = new Set<() => void>()

function getMediaQueryList() {
  if (typeof window === 'undefined' || !window.matchMedia) return null
  if (!mediaQueryList) {
    mediaQueryList = window.matchMedia(MOBILE_VIEWPORT_QUERY)
    currentMatches = mediaQueryList.matches
  }
  return mediaQueryList
}

function readViewportMatches() {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  if (mediaQueryList) return mediaQueryList.matches
  return window.matchMedia(MOBILE_VIEWPORT_QUERY).matches
}

function notifySubscribers() {
  subscribers.forEach(subscriber => subscriber())
}

function notifySubscribersSoon() {
  if (typeof queueMicrotask === 'function') {
    queueMicrotask(notifySubscribers)
    return
  }
  Promise.resolve().then(notifySubscribers)
}

function subscribeToViewport(onStoreChange: () => void) {
  const media = getMediaQueryList()
  if (!media) return () => {}

  subscribers.add(onStoreChange)

  const latestMatches = media.matches
  if (currentMatches !== latestMatches) {
    currentMatches = latestMatches
    notifySubscribersSoon()
  }

  if (!mediaListener) {
    mediaListener = event => {
      currentMatches = event.matches
      notifySubscribers()
    }

    if (media.addEventListener) {
      media.addEventListener('change', mediaListener)
    } else {
      media.addListener(mediaListener)
    }
  }

  return () => {
    subscribers.delete(onStoreChange)

    if (subscribers.size === 0 && mediaListener && mediaQueryList) {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener('change', mediaListener)
      } else {
        mediaQueryList.removeListener(mediaListener)
      }
      mediaListener = null
      mediaQueryList = null
      currentMatches = false
    }
  }
}

function getViewportSnapshot() {
  const latestMatches = readViewportMatches()
  currentMatches = latestMatches
  return latestMatches
}

function getServerSnapshot() {
  return false
}

export function useIsMobileViewport() {
  return useSyncExternalStore(subscribeToViewport, getViewportSnapshot, getServerSnapshot)
}
